import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Container,
  Header,
  Segment,
  Button,
  Divider,
  Message,
  Form,
  Input,
  Select,
  Table,
  Icon
} from 'semantic-ui-react';
import {
  initializePathDefsCache,
  getPathDefsCacheStatus,
  getAllPathDefinitions,
  getPathDefinition,
  buildPathFromDefinition,
  getResourceUrl
} from '../../utils/pathRegistryUtils';

/**
 * Example component demonstrating how to use the Path Registry utilities
 * This component is for demonstration purposes only and is not meant to be used in production
 */
const PathRegistryExample = () => {
  // Get user ID from Redux store
  const userId = useSelector(state => state.user.userData?.userId);

  // Local state
  const [cacheStatus, setCacheStatus] = useState(null);
  const [pathDefs, setPathDefs] = useState([]);
  const [selectedPathDef, setSelectedPathDef] = useState(null);
  const [params, setParams] = useState({});
  const [builtPath, setBuiltPath] = useState('');
  const [signedUrl, setSignedUrl] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Initialize cache on component mount
  useEffect(() => {
    const initCache = async () => {
      try {
        setLoading(true);
        await initializePathDefsCache();
        updateCacheStatus();
        loadPathDefs();
      } catch (error) {
        setError(`Error initializing cache: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    initCache();
  }, []);

  // Update cache status
  const updateCacheStatus = () => {
    const status = getPathDefsCacheStatus();
    setCacheStatus(status);
  };

  // Load path definitions
  const loadPathDefs = async () => {
    try {
      setLoading(true);
      const defs = await getAllPathDefinitions();
      setPathDefs(defs);
    } catch (error) {
      setError(`Error loading path definitions: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle path definition selection
  const handlePathDefSelect = async (e, { value }) => {
    try {
      setLoading(true);
      setError(null);

      // Clear previous values
      setSelectedPathDef(null);
      setParams({});
      setBuiltPath('');
      setSignedUrl('');

      // Get the selected path definition
      const pathDef = await getPathDefinition(value);
      setSelectedPathDef(pathDef);

      // Initialize params object with empty values for all parameters
      if (pathDef && pathDef.params) {
        const initialParams = {};
        pathDef.params.forEach(param => {
          initialParams[param.name] = '';
        });
        setParams(initialParams);
      }
    } catch (error) {
      setError(`Error selecting path definition: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle parameter change
  const handleParamChange = (e, { name, value }) => {
    setParams(prevParams => ({
      ...prevParams,
      [name]: value
    }));
  };

  // Build path from definition and parameters
  const handleBuildPath = () => {
    try {
      setError(null);

      if (!selectedPathDef) {
        setError('No path definition selected');
        return;
      }

      const path = buildPathFromDefinition(selectedPathDef, params);
      setBuiltPath(path);
    } catch (error) {
      setError(`Error building path: ${error.message}`);
    }
  };

  // Get signed URL for the built path
  const handleGetSignedUrl = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!userId) {
        setError('User ID is required');
        return;
      }

      if (!selectedPathDef) {
        setError('No path definition selected');
        return;
      }

      const url = await getResourceUrl(userId, selectedPathDef.name, params);
      setSignedUrl(url);
    } catch (error) {
      setError(`Error getting signed URL: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Refresh cache
  const handleRefreshCache = async () => {
    try {
      setLoading(true);
      setError(null);

      await initializePathDefsCache();
      updateCacheStatus();
      loadPathDefs();
    } catch (error) {
      setError(`Error refreshing cache: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Generate path definition options for dropdown
  const pathDefOptions = pathDefs.map(def => ({
    key: def.name,
    text: def.name,
    value: def.name,
    description: def.description || 'No description'
  }));

  return (
    <Container>
      <Header as="h1">Path Registry Example</Header>
      <Segment loading={loading}>
        <Header as="h2">Cache Status</Header>
        {cacheStatus && (
          <Table definition>
            <Table.Body>
              <Table.Row>
                <Table.Cell>Initialized</Table.Cell>
                <Table.Cell>{cacheStatus.initialized ? 'Yes' : 'No'}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Last Refresh</Table.Cell>
                <Table.Cell>{cacheStatus.lastRefresh || 'Never'}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Size</Table.Cell>
                <Table.Cell>{cacheStatus.size} path definitions</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>TTL</Table.Cell>
                <Table.Cell>{cacheStatus.ttlHours} hours</Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        )}
        <Button primary onClick={handleRefreshCache}>
          <Icon name="refresh" />
          Refresh Cache
        </Button>
      </Segment>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      <Segment loading={loading}>
        <Header as="h2">Path Definition Selection</Header>
        <Form>
          <Form.Field>
            <label>Select Path Definition</label>
            <Select
              placeholder="Select a path definition"
              options={pathDefOptions}
              onChange={handlePathDefSelect}
              fluid
            />
          </Form.Field>
        </Form>

        {selectedPathDef && (
          <>
            <Divider />
            <Header as="h3">Selected Path Definition: {selectedPathDef.name}</Header>
            <p><strong>Description:</strong> {selectedPathDef.description || 'No description'}</p>
            <p><strong>Required Segments:</strong> {selectedPathDef.pattern.requiredSegments.join(', ')}</p>
            <p><strong>Folder Template:</strong> {selectedPathDef.pattern.folderTemplate}</p>

            <Header as="h4">Parameters</Header>
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Name</Table.HeaderCell>
                  <Table.HeaderCell>Position</Table.HeaderCell>
                  <Table.HeaderCell>Required</Table.HeaderCell>
                  <Table.HeaderCell>Critical</Table.HeaderCell>
                  <Table.HeaderCell>Default Value</Table.HeaderCell>
                  <Table.HeaderCell>Description</Table.HeaderCell>
                  <Table.HeaderCell>Value</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {selectedPathDef.params.map(param => (
                  <Table.Row key={param.name}>
                    <Table.Cell>{param.name}</Table.Cell>
                    <Table.Cell>{param.position}</Table.Cell>
                    <Table.Cell>
                      {param.required ? <Icon name="check" color="green" /> : <Icon name="x" color="red" />}
                    </Table.Cell>
                    <Table.Cell>
                      {param.isCritical ? <Icon name="check" color="green" /> : <Icon name="x" color="red" />}
                    </Table.Cell>
                    <Table.Cell>
                      {param.defaultValue !== undefined && param.defaultValue !== '' ? param.defaultValue : <em>None</em>}
                    </Table.Cell>
                    <Table.Cell>{param.description || <em>No description</em>}</Table.Cell>
                    <Table.Cell>
                      <Input
                        name={param.name}
                        placeholder={`Enter ${param.name}`}
                        value={params[param.name] || ''}
                        onChange={handleParamChange}
                        fluid
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            <Form>

              <Button primary onClick={handleBuildPath}>
                <Icon name="code" />
                Build Path
              </Button>
            </Form>

            {builtPath && (
              <>
                <Divider />
                <Header as="h4">Built Path</Header>
                <Segment>
                  <code>{builtPath}</code>
                </Segment>

                <Button color="green" onClick={handleGetSignedUrl}>
                  <Icon name="linkify" />
                  Get Signed URL
                </Button>
              </>
            )}

            {signedUrl && (
              <>
                <Divider />
                <Header as="h4">Signed URL</Header>
                <Segment>
                  <p style={{ wordBreak: 'break-all' }}>{signedUrl}</p>
                </Segment>

                {signedUrl.toLowerCase().endsWith('.jpg') ||
                  signedUrl.toLowerCase().endsWith('.jpeg') ||
                  signedUrl.toLowerCase().endsWith('.png') ||
                  signedUrl.toLowerCase().endsWith('.gif') ? (
                  <>
                    <Header as="h4">Image Preview</Header>
                    <Segment>
                      <img
                        src={signedUrl}
                        alt="Resource preview"
                        style={{ maxWidth: '100%', maxHeight: '300px' }}
                      />
                    </Segment>
                  </>
                ) : signedUrl.toLowerCase().endsWith('.mp3') ||
                  signedUrl.toLowerCase().endsWith('.wav') ||
                  signedUrl.toLowerCase().endsWith('.ogg') ? (
                  <>
                    <Header as="h4">Audio Preview</Header>
                    <Segment>
                      <audio controls style={{ width: '100%' }}>
                        <source src={signedUrl} />
                        Your browser does not support the audio element.
                      </audio>
                    </Segment>
                  </>
                ) : null}
              </>
            )}
          </>
        )}
      </Segment>
    </Container>
  );
};

export default PathRegistryExample;
